import React, { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import type { InteractiveElement } from '~/stores/interactiveElements.store';

interface Props {
  element: InteractiveElement;
  deviceId: string;
}

const QRWrapper = styled.div<{
  $x: number;
  $y: number;
  $w: number;
  $h: number;
}>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $w }) => $w}%;
  height: ${({ $h }) => $h}%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const QRContainer = styled.div<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`;

/**
 * Wrapper do QR que NÃO toma o eixo Y inteiro — deixa caption respirar.
 * Antes: QRCodeSVG tinha {flex: 1} direto no style, comendo todo o espaço
 * residual; caption ficava com 12-14px e nowrap+ellipsis.
 */
const QRBox = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Caption embaixo do QR.
 *
 * <p>Renato (2026-06-01): caption longa precisa quebrar em linhas, não
 * cortar com "...". Texto sempre centralizado. Permite até 3 linhas com
 * line-clamp; em fontes pequenas no Tab A7 isso dá ~3×16px = 48px.</p>
 *
 * <p>font-size escalado por clamp(): mínimo legível (11px), preferencial
 * proporcional ao viewport (~1.1vmin), máximo 18px pra não engolir o QR
 * em telas grandes (TVs).</p>
 */
const QRLabel = styled.span<{ $color: string }>`
  flex-shrink: 0;
  font-size: clamp(11px, 1.1vmin, 18px);
  font-weight: 600;
  color: ${({ $color }) => $color};
  text-align: center;
  line-height: 1.25;
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InteractiveQRCode: React.FC<Props> = ({ element, deviceId }) => {
  const [isActive, setIsActive] = useState(element.inSeconds <= 0);

  useEffect(() => {
    const timers: number[] = [];
    if (element.inSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(true), element.inSeconds * 1000),
      );
    }
    if (element.outSeconds !== null && element.outSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(false), element.outSeconds * 1000),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [element.inSeconds, element.outSeconds]);

  const qrValue = useMemo(() => {
    const base = element.qrRenderUrl || element.qrUrl;
    if (!base) return '';
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}pid=${deviceId}`;
  }, [element.qrRenderUrl, element.qrUrl, deviceId]);

  if (!qrValue) return null;

  // QR é SVG quadrado — o min(100%, altura disponível) garante que ele cabe
  // dentro do QRBox sem distorcer, mesmo quando o caption ocupa 2-3 linhas.
  const qrSize = '100%';

  return (
    <QRWrapper
      $x={element.xPct}
      $y={element.yPct}
      $w={element.wPct}
      $h={element.hPct}
      style={{ opacity: isActive ? 1 : 0 }}
    >
      <QRContainer $bg={element.bgColor}>
        <QRBox>
          <QRCodeSVG
            value={qrValue}
            size={undefined}
            style={{ width: qrSize, height: qrSize, maxHeight: '100%' }}
            bgColor={element.bgColor}
            fgColor={element.fgColor}
            level={element.qrEccLevel}
          />
        </QRBox>
        {element.label && (
          <QRLabel $color={element.textColor}>{element.label}</QRLabel>
        )}
      </QRContainer>
    </QRWrapper>
  );
};

export default InteractiveQRCode;
